import api from '../services/api';
import { setKnownPageSlugs } from './knownPages';
import { isPreviewMode } from './previewBridge';

// Declare global window type for embedded config
declare global {
  interface Window {
    __STOREFRONT_CONFIG__?: any;
    __STOREFRONT_CONFIG_EMBEDDED__?: boolean;
  }
}

const storefrontConfigCache = new Map<string, any>();
const storefrontConfigInflight = new Map<string, Promise<any | null>>();
let latestStorefrontConfig: any | null = null;

const normalizeDomain = (domain?: string): string => {
  if (domain && typeof domain === 'string') return domain.trim().toLowerCase();
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return String(window.location.hostname).trim().toLowerCase();
  }
  return '';
};

const normalizeFunnelSlug = (funnelSlug?: string): string =>
  String(funnelSlug || '').trim().toLowerCase();

const normalizeTemplateToken = (value: unknown): string =>
  String(value || '').trim().toLowerCase();

const getCacheKey = (domain?: string, funnelSlug?: string): string =>
  `${normalizeDomain(domain)}::${normalizeFunnelSlug(funnelSlug)}`;

const cacheStorefrontConfig = (domain: string, funnelSlug: string, config: any) => {
  if (!config) return;
  storefrontConfigCache.set(`${domain}::${funnelSlug}`, config);
  if (!funnelSlug) {
    storefrontConfigCache.set(`${domain}::`, config);
  }
  latestStorefrontConfig = config;

  // Record which pages this tenant actually published, so link renderers can
  // drop menu entries that would 404. Every config load funnels through here.
  try {
    const pages = Array.isArray(config?.pages) ? config.pages : [];
    setKnownPageSlugs(pages.map((p: any) => p?.slug));
  } catch { /* never block config caching on this */ }

  // Expose latest config to non-module consumers (e.g. api.ts auto-injects
  // websiteId for product calls without needing per-call wiring).
  try {
    (window as any).__STOREFRONT_CONFIG_LATEST__ = config;
  } catch { /* ignore */ }
};

function resolveEmbeddedStorefrontConfig(funnelSlug?: string): any | null {
  if (!window.__STOREFRONT_CONFIG_EMBEDDED__ || !window.__STOREFRONT_CONFIG__) {
    return null;
  }

  const embeddedConfig = window.__STOREFRONT_CONFIG__;
  const isComplete =
    embeddedConfig.template ||
    embeddedConfig.sections ||
    embeddedConfig.storefrontConfig;

  if (funnelSlug) {
    if (embeddedConfig.funnelSlug === funnelSlug || embeddedConfig.slug === funnelSlug) {
      return embeddedConfig;
    }

    if (Array.isArray(embeddedConfig.funnels)) {
      const matchedFunnel = embeddedConfig.funnels.find(
        (f: any) => f.funnelSlug === funnelSlug || f.slug === funnelSlug
      );
      if (matchedFunnel) {
        return matchedFunnel;
      }
    }

    return null;
  }

  return isComplete ? embeddedConfig : null;
}

/**
 * Returns embedded storefront config immediately (synchronous) when available.
 * Useful for preventing first-paint UI flicker before async config loading completes.
 */
export function getEmbeddedStorefrontConfigSync(funnelSlug?: string): any | null {
  return resolveEmbeddedStorefrontConfig(funnelSlug);
}

/**
 * Returns cached storefront config immediately (synchronous), including embedded config.
 * Useful to avoid first-paint fallback flashes between route transitions.
 */
export function getCachedStorefrontConfigSync(domain?: string, funnelSlug?: string): any | null {
  // In preview iframe, prefer the latest config the builder pushed via
  // postMessage (stored in sessionStorage by previewBridge). Lets sync
  // consumers (e.g. NewsletterFooter) reflect live edits without reload.
  try {
    if (typeof window !== 'undefined') {
      // isPreviewMode() rather than the raw query flag: the flag only exists on
      // the URL the admin opens, so navigating inside the preview frame used to
      // fall through and fetch the sandbox tenant's own config.
      if (isPreviewMode()) {
        const raw = sessionStorage.getItem('__DIVI_PREVIEW_CONFIG__');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') return parsed;
        }
      }
    }
  } catch { /* ignore */ }

  const embeddedConfig = resolveEmbeddedStorefrontConfig(funnelSlug);
  if (embeddedConfig) return embeddedConfig;

  const domainKey = normalizeDomain(domain);
  const slugKey = normalizeFunnelSlug(funnelSlug);

  const exact = storefrontConfigCache.get(`${domainKey}::${slugKey}`);
  if (exact) return exact;

  if (!slugKey) {
    const main = storefrontConfigCache.get(`${domainKey}::`);
    if (main) return main;
  }

  return latestStorefrontConfig;
}

/**
 * Get storefront configuration
 * Priority:
 * 1. Embedded config (from build) - no API call needed
 * 2. API call (for development or when embedded config not available)
 */
export async function getStorefrontConfig(domain?: string, funnelSlug?: string): Promise<any | null> {
  const domainKey = normalizeDomain(domain);
  const slugKey = normalizeFunnelSlug(funnelSlug);
  const cacheKey = getCacheKey(domainKey, slugKey);

  // Preview iframe: latest builder postMessage payload wins over embedded
  // config so live edits surface without reload.
  try {
    if (typeof window !== 'undefined') {
      // isPreviewMode() rather than the raw query flag: the flag only exists on
      // the URL the admin opens, so navigating inside the preview frame used to
      // fall through and fetch the sandbox tenant's own config.
      if (isPreviewMode()) {
        const raw = sessionStorage.getItem('__DIVI_PREVIEW_CONFIG__');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') return parsed;
        }
      }
    }
  } catch { /* ignore */ }

  const embeddedConfig = resolveEmbeddedStorefrontConfig(slugKey);
  if (embeddedConfig) {
    cacheStorefrontConfig(domainKey, slugKey, embeddedConfig);
    return embeddedConfig;
  }

  const cachedConfig = getCachedStorefrontConfigSync(domainKey, slugKey);
  if (cachedConfig) return cachedConfig;

  const inflight = storefrontConfigInflight.get(cacheKey);
  if (inflight) return inflight;

  // ... (script tag check skipped for brevity, but same logic applies)

  // Fallback to API call (for development or when embedded config not available)
  const requestPromise = (async () => {
    try {
      const targetDomain = domainKey || window.location.hostname;
      const endpoint = slugKey
        ? `/storefront/funnel/${slugKey}?domain=${targetDomain}`
        : `/storefront/config?domain=${targetDomain}`;

      const response = await api.get(endpoint);
      const responseData = response.data;
      cacheStorefrontConfig(targetDomain, slugKey, responseData);
      return responseData;
    } catch (error) {
      console.error('[storefrontConfig] API Failed:', error);
      return null;
    } finally {
      storefrontConfigInflight.delete(cacheKey);
    }
  })();

  storefrontConfigInflight.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Check if storefront config is embedded (static build)
 */
export function isStorefrontConfigEmbedded(): boolean {
  return !!(window.__STOREFRONT_CONFIG_EMBEDDED__ && window.__STOREFRONT_CONFIG__);
}

/**
 * Detect whether a storefront config should behave as a single-page storefront.
 * Supports legacy "single-page" plus newer slug variants like "ember-single-page".
 */
export function isSinglePageTemplateConfig(config: any): boolean {
  if (!config || typeof config !== 'object') return false;

  const template = config.template || {};
  const templateBaseConfig = template.baseConfig || {};
  const defaultSections = config.defaultSections || template.defaultSections || templateBaseConfig.defaultSections || {};

  if (defaultSections.isSinglePage === true) {
    return true;
  }

  const slug = normalizeTemplateToken(template.slug || config.slug);
  const name = normalizeTemplateToken(template.name || config.name);
  const selectedLayout = normalizeTemplateToken(config.selectedLayout || template.selectedLayout || templateBaseConfig.selectedLayout);
  const category = normalizeTemplateToken(template.category || config.category || templateBaseConfig.category);
  const templateType = normalizeTemplateToken(config.templateType || template.templateType || templateBaseConfig.templateType);

  const matchesSinglePageToken = (value: string): boolean =>
    value === 'single-page' ||
    value === 'single page' ||
    value.endsWith('-single-page') ||
    value.endsWith(' single-page') ||
    value.endsWith(' single page');

  if (matchesSinglePageToken(slug)) return true;
  if (matchesSinglePageToken(selectedLayout)) return true;
  if (matchesSinglePageToken(category)) return true;
  if (matchesSinglePageToken(templateType)) return true;
  if (name.includes('single-page') || name.includes('single page')) return true;

  return false;
}

